import React from "react";
import { ArrowLeft, PieChart, UserRound, Pencil, Wallet, Trash2, UserPlus, MapPin, CalendarDays, Building2 } from "lucide-react";
import { statusInfo, monthPct } from "./allocUtils";
import { BudgetBar } from "./BudgetBar";
import { TeamList } from "./TeamList";
import { eur, fmtDate } from "../pnl/format";

const actBtn = "h-9 px-3 rounded-lg border border-neutral-200 bg-white text-[13px] flex items-center gap-1.5 hover:border-brand hover:text-brand transition-colors";

function Stat({ label, value, sub, tone = "text-ink", testid }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-display font-bold text-2xl tabular-nums mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

export function ProjectDetailPanel({ p, rows, pnl, load, overCount, perms, onBack, actions }) {
  const st = statusInfo(p.status);
  const fte = rows.reduce((s, a) => s + monthPct(a), 0) / 100;
  return (
    <div className="space-y-5" data-testid={`project-detail-${p.code}`}>
      <button type="button" onClick={onBack} data-testid="back-to-projects-list"
        className="lg:hidden flex items-center gap-1.5 text-sm text-brand font-medium"><ArrowLeft className="h-4 w-4" /> Înapoi la lista proiectelor</button>

      <div className="bg-white border border-neutral-200 rounded-2xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] text-ink-muted">{p.code}</span>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${st.cls}`}><span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}</span>
            </div>
            <h2 className="font-display font-bold text-2xl md:text-[28px] tracking-tight text-ink mt-1 leading-tight">{p.name}</h2>
            <div className="mt-2 flex items-center gap-x-4 gap-y-1 flex-wrap text-[13px] text-ink-muted">
              {p.client_name && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{p.client_name}</span>}
              {p.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</span>}
              <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {perms.pnl && <button data-testid={`pnl-open-${p.code}`} onClick={actions.pnl} className={actBtn}><PieChart className="h-3.5 w-3.5" /> P&amp;L</button>}
          {perms.client && <button data-testid={`client-portal-${p.code}`} onClick={actions.client} className={actBtn}><UserRound className="h-3.5 w-3.5" /> Portal client{(p.client_user_ids || []).length ? ` (${p.client_user_ids.length})` : ""}</button>}
          {perms.manage && <button data-testid={`project-edit-${p.code}`} onClick={actions.edit} className={actBtn}><Pencil className="h-3.5 w-3.5" /> Editează</button>}
          {perms.manage && <button data-testid={`fin-open-${p.code}`} onClick={actions.fin} className={actBtn}><Wallet className="h-3.5 w-3.5" /> Finanțe</button>}
          {perms.manage && <button data-testid={`project-delete-${p.code}`} onClick={actions.remove} className={`${actBtn} hover:!border-red-500 hover:!text-red-600 ml-auto`}><Trash2 className="h-3.5 w-3.5" /> Șterge</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Echipă" value={rows.length} sub={`${fte.toFixed(1)} FTE luna aceasta`} testid="detail-team" />
        <Stat label="Buget" value={p.budget_amount ? eur(p.budget_amount) : "—"} sub={(p.budget_periods || []).length ? `${p.budget_periods.length} perioade` : "fără perioade"} testid={`project-budget-${p.code}`} />
        <Stat label="Supraalocați" value={overCount} tone={overCount ? "text-red-600" : "text-ink"} sub="peste 100% luna aceasta" testid="detail-overalloc" />
        <div className="bg-white border border-neutral-200 rounded-xl p-4 col-span-2 md:col-span-1 flex flex-col justify-center" data-testid="detail-budget-bar">
          {pnl !== undefined ? <BudgetBar totals={pnl?.totals} /> : <div className="text-[12px] text-ink-muted">Consumul de buget e vizibil cu drept de rapoarte.</div>}
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl p-5 md:p-6">
        <div className="flex items-center gap-3 flex-wrap pb-5 mb-5 border-b border-neutral-100">
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-ink">Project Manager</div>
            <div className="text-[12px] text-ink-muted">Aprobă cererile de acces după Managerul Superior.</div>
          </div>
          <select value={p.project_manager_id || ""} onChange={(e) => actions.setPm(e.target.value)} disabled={!perms.manage}
            data-testid={`pm-select-${p.code}`}
            className="w-full sm:w-auto sm:min-w-[260px] h-10 px-3 rounded-lg border border-neutral-200 bg-white text-sm focus:border-brand focus:outline-none disabled:opacity-60">
            <option value="">— fără PM desemnat —</option>
            {rows.map((a) => <option key={a.employee_id} value={a.employee_id}>{a.employee?.name}{a.employee?.job_title ? ` (${a.employee.job_title})` : ""}</option>)}
          </select>
        </div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="font-display font-semibold text-ink">Echipă & alocări</div>
            <div className="text-[12px] text-ink-muted">Apasă pe un membru ca să-i editezi alocarea, perioadele și tariful.</div>
          </div>
          <button data-testid={`assign-manage-${p.code}`} onClick={actions.addMembers}
            className="h-9 px-3 rounded-lg bg-brand text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-[#2f5290] transition-colors shrink-0">
            <UserPlus className="h-4 w-4" /> Adaugă membri
          </button>
        </div>
        <TeamList rows={rows} load={load} pmId={p.project_manager_id} onOpen={actions.openMember} />
      </div>
    </div>
  );
}
